import { useEffect, useMemo, useState } from 'react';
import { integrationApi, payablesApi, templatesApi } from '../../api/services';
import type { IntegrationOperationStatusDto, PaymentTypeDto } from '../../api/types';
import { Badge, Button, Card, Field, Loadable, Select, Table, useAction, useAsync } from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { INTEGRATION_OPERATIONS, isIntegrationOperation } from './integrationScope';

interface Line { accountNumber: string; concept: string; debitCredit: 'D' | 'C' }

const EMPTY_LINES: Line[] = [
  { accountNumber: '', concept: '', debitCredit: 'D' },
  { accountNumber: '', concept: '', debitCredit: 'C' },
];

/** Orden en pantalla: facturación y pagos primero, luego cajas. */
const ORDER = INTEGRATION_OPERATIONS;

/** Anulaciones: no llevan plantilla; la contabilidad hace el reverso exacto del asiento original. */
const CANCELLATIONS = [
  { value: 'ANULACION_FACTURA', label: 'Anulación de factura (facturación)', of: 'la factura' },
  { value: 'ANULACION_PAGO', label: 'Anulación de pago (facturación)', of: 'el pago' },
];

/**
 * Editor de una sola pantalla: se elige la operación (y el tipo de pago) y se arma la plantilla línea por línea:
 * Cuenta → Campo → Débito/Crédito. Al guardar, la plantilla se crea o actualiza y queda asignada a la operación.
 */
export function OperationTemplateEditor() {
  const ops = useAsync(() => integrationApi.operations(), []);
  const paymentTypes = useAsync<PaymentTypeDto[]>(() => payablesApi.paymentTypes().catch(() => []), []);
  const [baseOp, setBaseOp] = useState<string>('FC_FACTURA');
  const [paymentType, setPaymentType] = useState<string>('');

  // Sólo operaciones de integración: las demás se asignan en Plantillas → "Plantillas por operación".
  const all = (ops.data ?? []).filter(isIntegrationOperation);
  const bases = useMemo(() => all.filter((o) => !o.paymentType)
    .sort((a, b) => (ORDER.indexOf(a.operation) + 1 || 99) - (ORDER.indexOf(b.operation) + 1 || 99)), [all]);
  const cancellation = CANCELLATIONS.find((c) => c.value === baseOp);
  const base = bases.find((o) => o.operation === baseOp);
  const code = paymentType && base?.supportsPaymentTypes ? `${baseOp}@${paymentType}` : baseOp;
  const own = all.find((o) => o.operation === code);
  // Sin plantilla propia para el tipo de pago: se parte de la plantilla por defecto.
  const source: IntegrationOperationStatusDto | undefined = own?.templateCode ? own : (code !== baseOp ? base : undefined);

  return (
    <Loadable state={ops}>
      {() => (
        <>
          <Card>
            <div className="acc-grid-form">
              <Field label="Operación">
                <Select value={baseOp} onChange={(v) => { setBaseOp(v); setPaymentType(''); }}
                  options={[...bases.map((o) => ({ value: o.operation, label: o.name })), ...CANCELLATIONS.map((c) => ({ value: c.value, label: c.label }))]} />
              </Field>
              {!cancellation && base?.supportsPaymentTypes && (
                <Field label="Tipo de pago" hint="Vacío = plantilla para todos los tipos de pago">
                  <Select value={paymentType} onChange={setPaymentType} placeholder="Todos (por defecto)"
                    options={(paymentTypes.data ?? []).map((t) => ({ value: t.id.trim(), label: `${t.id.trim()} · ${t.description}` }))} />
                </Field>
              )}
              <Field label="Estado">
                <div style={{ paddingTop: 8 }}>
                  {cancellation
                    ? <Badge tone="ok">Automática</Badge>
                    : own?.source === 'DB'
                    ? <Badge tone={own.templateActive === false ? 'bad' : 'ok'}>{own.templateActive === false ? 'Plantilla inactiva' : `Configurada · plantilla ${own.templateCode}`}</Badge>
                    : code !== baseOp && base?.source === 'DB'
                      ? <Badge tone="warn">Usa la plantilla por defecto</Badge>
                      : <Badge>Sin configurar</Badge>}
                </div>
              </Field>
            </div>
          </Card>
          {cancellation && (
            <Card title={cancellation.label}>
              <p>
                No requiere configuración. Al anular {cancellation.of}, la contabilidad registra automáticamente el reverso exacto
                de su asiento: las mismas cuentas y montos, con los débitos y créditos invertidos. Así la anulación siempre cuadra
                con el asiento original, aunque después cambie la plantilla.
              </p>
            </Card>
          )}
          {!cancellation && base && <LinesEditor key={code} code={code} op={base} source={source} onSaved={ops.reload}
            paymentTypeName={paymentType ? (paymentTypes.data ?? []).find((t) => t.id.trim() === paymentType)?.description ?? paymentType : undefined} />}
          <OperationsSummary rows={all} onPick={(o) => { setBaseOp(o.baseOperation || o.operation); setPaymentType(o.paymentType ?? ''); }}
            onPickCancellation={(v) => { setBaseOp(v); setPaymentType(''); }} />
        </>
      )}
    </Loadable>
  );
}

function LinesEditor({ code, op, source, onSaved, paymentTypeName }: {
  code: string; op: IntegrationOperationStatusDto; source?: IntegrationOperationStatusDto; onSaved: () => void; paymentTypeName?: string;
}) {
  const [lines, setLines] = useState<Line[]>(EMPTY_LINES);
  const [loading, setLoading] = useState(false);
  const { run, busy } = useAction();

  // Carga las líneas de la plantilla actual (o de la plantilla por defecto si el tipo de pago no tiene propia).
  useEffect(() => {
    if (!source?.templateCode) { setLines(EMPTY_LINES); return; }
    let alive = true;
    setLoading(true);
    templatesApi.get(source.moduleCode, source.templateCode)
      .then((t) => {
        if (!alive) return;
        setLines(t.lines.map((l) => ({ accountNumber: l.accountNumber, concept: (l.concept ?? '').toUpperCase(), debitCredit: l.debitCredit === 'C' ? 'C' : 'D' })));
      })
      .catch(() => { if (alive) setLines(EMPTY_LINES); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [source?.moduleCode, source?.templateCode]);

  const fieldOptions = op.conceptList.map((c) => ({ value: c, label: op.conceptLabels?.[c] ?? c }));
  const setLine = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  const save = () => run(async () => {
    const filled = lines.filter((l) => l.accountNumber.trim());
    if (filled.length < 2) throw new Error('Agregue al menos dos líneas con cuenta.');
    if (!filled.some((l) => l.concept)) throw new Error('Elija el campo de cada línea.');
    await integrationApi.saveOperationTemplate(code, {
      entryTypeId: null, lines: filled.map((l) => ({ accountNumber: l.accountNumber.trim(), debitCredit: l.debitCredit, concept: l.concept || null })),
    });
    onSaved();
  }, { success: 'Plantilla guardada y asignada.' });

  return (
    <Card title={`Cuentas de: ${op.name}${paymentTypeName ? ` — ${paymentTypeName}` : ''}`}>
      {loading ? <p>Cargando…</p> : (
        <>
          <Table rows={lines.map((l, i) => ({ ...l, i }))} rowKey={(l) => l.i} columns={[
            { key: 'a', title: 'Cuenta', render: (l) => <AccountPicker value={l.accountNumber} onChange={(v) => setLine(l.i, { accountNumber: v })} /> },
            { key: 'c', title: 'Campo (qué monto va)', render: (l) => <Select value={l.concept} onChange={(v) => setLine(l.i, { concept: v })} placeholder="Elija el campo" options={fieldOptions} /> },
            { key: 'd', title: 'Afecta', render: (l) => <Select value={l.debitCredit} onChange={(v) => setLine(l.i, { debitCredit: v })} options={[{ value: 'D', label: 'Débito' }, { value: 'C', label: 'Crédito' }]} /> },
            { key: 'x', title: '', render: (l) => <Button kind="link" disabled={lines.length <= 2} onClick={() => setLines((ls) => ls.filter((_, k) => k !== l.i))}>Quitar</Button> },
          ]} />
          <div className="acc-actions" style={{ justifyContent: 'flex-start' }}>
            <Button onClick={() => setLines((ls) => [...ls, { accountNumber: '', concept: '', debitCredit: 'D' }])}>Agregar línea</Button>
          </div>

          <div className="acc-actions"><Button kind="primary" busy={busy} onClick={save}>Guardar</Button></div>
        </>
      )}
    </Card>
  );
}

function OperationsSummary({ rows, onPick, onPickCancellation }: {
  rows: IntegrationOperationStatusDto[]; onPick: (o: IntegrationOperationStatusDto) => void; onPickCancellation: (value: string) => void;
}) {
  const sorted = [...rows].sort((a, b) => {
    const ka = (ORDER.indexOf(a.baseOperation || a.operation) + 1 || 99) * 1000;
    const kb = (ORDER.indexOf(b.baseOperation || b.operation) + 1 || 99) * 1000;
    return ka - kb || a.operation.localeCompare(b.operation);
  });
  return (
    <Card title="Resumen de operaciones">
      <Table rows={sorted} rowKey={(o) => o.operation} onRowClick={onPick} columns={[
        { key: 'n', title: 'Operación', render: (o) => (o.paymentType ? <span style={{ paddingLeft: 16 }}>↳ {o.name}</span> : <strong>{o.name}</strong>) },
        { key: 's', title: 'Estado', render: (o) => (o.source === 'DB' ? <Badge tone={o.templateActive === false ? 'bad' : 'ok'}>{o.templateActive === false ? 'Plantilla inactiva' : 'Configurada'}</Badge> : o.source === 'APPSETTINGS' ? <Badge tone="warn">Configuración del servidor</Badge> : <Badge>Sin configurar</Badge>) },
        { key: 't', title: 'Plantilla', render: (o) => (o.templateCode ? `${o.templateCode} · ${o.templateDescription ?? ''}` : '') },
      ]} />
      <Table rows={CANCELLATIONS} rowKey={(c) => c.value} onRowClick={(c) => onPickCancellation(c.value)} columns={[
        { key: 'n', title: 'Anulación', render: (c) => <strong>{c.label}</strong> },
        { key: 's', title: 'Estado', render: () => <Badge tone="ok">Automática</Badge> },
        { key: 't', title: 'Cómo se registra', render: () => 'Reverso exacto del asiento original' },
      ]} />
      <p><small>Haga clic en una operación para editar sus cuentas.</small></p>
    </Card>
  );
}
